import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Droplet, AlertTriangle, Activity, Pill, Scissors, Baby, Syringe,
    ShieldCheck, UserRound, Ban, Building2, HeartPulse,
    Gauge, ClipboardCheck, CalendarDays, Stethoscope, Clock, House, Loader2,
    Milestone, Route, Flag, CircleCheck, CircleAlert, CircleDashed, Hourglass,
    CalendarCheck, CalendarClock, UserCheck,
} from 'lucide-react';
import { getHealthRecord, getHealthReadings, getHealthProgrammes } from '../../redux/mocSlice';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { formatTime as clockTime } from '../../utils/clock';
import CommonLoader from '../CommonLoader';

// THE PATIENT HEALTH RECORD, SEEN FROM THE PLATFORM
// (doc/one-patient-row.md §8, Phase 3).
//
// ONE RECORD, NO HOSPITAL PICKER. `health_entries` carries no `tenantId` at
// all — the record belongs to the PERSON, which is why the tenant guard never
// filters it and why utils/healthAccess.js is the whole access rule. So this
// tab sits with Profile and Dependencies (person-level) and NOT with the
// clinical tabs: a hospital selector above it would be a control that cannot
// change the answer. Each card names the hospital the entry was recorded at
// instead, which is the honest version of the same information.
//
// READ-ONLY, AND NOT MERELY DISABLED. The platform has no verb here: Verify
// (confirm) belongs to a doctor who has treated this person, Add and "Mark as
// incorrect" belong to the patient and the recorder. Nothing is ever deleted
// (there is no destroy path in the API), so a retracted row STAYS in the list
// greyed with its reason — dropping it would make a retraction look like a
// deletion, which is exactly what this record refuses to allow.
//
// Super Admin reads it as a named reader in healthAccess (beside the patient,
// their family and a linked doctor), so this is the same endpoint and the same
// payload every other client gets. The read writes a `health_record_read`
// audit row server-side, which is why the note below says so out loud.
//
// CHECK READINGS (doc/check-sets-plan.md §6, Round A) are the eighth block,
// but NOT an eighth category: they come from their own endpoint
// (GET /health-record/:patientId/readings), grouped per visit — date,
// hospital, doctor — and each value carries the label, type and unit of the
// set AS IT WAS when the reading was taken. Nothing here knows what a set
// "should" contain: every set and field is defined by a doctor or a hospital,
// so the section renders whatever the reading says, for any speciality.
//
// CARE PROGRAMMES (doc/care-programmes-plan.md, Round B) are the ninth block,
// again from their own endpoint (GET /health-record/:patientId/programmes):
// every programme started for the person at a hospital they are still with,
// running ones first, closed ones below with their outcome. Each carries the
// programme AS IT WAS STARTED and a timeline the SERVER computes on every
// read (week, due dates, done / due / late / upcoming, and what proved a
// milestone done). The page renders that timeline and never recomputes it —
// and, like every block here, has no verb: Start, Tick and Close belong to
// the hospital's doctor.

/* ---------------------------------------------------------------- *
 * Vocabulary — module level, so a card is not a new type per render
 * ---------------------------------------------------------------- */

// The seven sections, in the order §8 names them. `id` is the API's category
// key; every section is rendered even when empty, because the response always
// carries all seven and a missing heading would read as "not asked about".
const SECTIONS = [
    { id: 'blood_event', icon: Droplet, label: ['moc.hr_cat_blood_event', 'Blood'], empty: ['moc.hr_empty_blood_event', 'No donation, transfusion or transplant on record.'] },
    { id: 'allergy', icon: AlertTriangle, label: ['moc.hr_cat_allergy', 'Allergies'], empty: ['moc.hr_empty_allergy', 'No allergy on record.'] },
    { id: 'condition', icon: Activity, label: ['moc.hr_cat_condition', 'Conditions'], empty: ['moc.hr_empty_condition', 'No long-term condition on record.'] },
    { id: 'medicine', icon: Pill, label: ['moc.hr_cat_medicine', 'Medicines'], empty: ['moc.hr_empty_medicine', 'No medicine on record.'] },
    { id: 'surgery', icon: Scissors, label: ['moc.hr_cat_surgery', 'Surgeries'], empty: ['moc.hr_empty_surgery', 'No surgery on record.'] },
    { id: 'life_event', icon: Baby, label: ['moc.hr_cat_life_event', 'Life events'], empty: ['moc.hr_empty_life_event', 'No life event on record.'] },
    { id: 'vaccination', icon: Syringe, label: ['moc.hr_cat_vaccination', 'Vaccinations'], empty: ['moc.hr_empty_vaccination', 'No vaccination on record.'] },
];

const SUBTYPES = {
    donation: ['moc.hr_subtype_donation', 'Donation'],
    transfusion: ['moc.hr_subtype_transfusion', 'Transfusion'],
    transplant: ['moc.hr_subtype_transplant', 'Transplant'],
    childbirth_normal: ['moc.hr_subtype_childbirth_normal', 'Childbirth (normal)'],
    childbirth_csection: ['moc.hr_subtype_childbirth_csection', 'Childbirth (caesarean)'],
    pregnancy: ['moc.hr_subtype_pregnancy', 'Pregnancy'],
    other: ['moc.hr_subtype_other', 'Other'],
    prescribed: ['moc.hr_subtype_prescribed', 'Prescribed'],
    own: ['moc.hr_subtype_own', 'Added by the patient'],
};

// 'incorrect' is deliberately absent: a retracted row wears the "Marked
// incorrect" badge instead, so it is never mistaken for one more status chip.
const STATUSES = {
    active: ['moc.hr_status_active', 'Active'],
    resolved: ['moc.hr_status_resolved', 'Resolved'],
    current: ['moc.hr_status_current', 'Currently taking'],
    stopped: ['moc.hr_status_stopped', 'Stopped'],
};

// `details` is a per-category JSONB blob. The known fields are labelled; an
// unknown one is prettified rather than dropped, so a field the API adds
// later shows up instead of silently disappearing from a medical record.
const DETAIL_LABELS = {
    reaction: ['moc.hr_detail_reaction', 'Reaction'],
    severity: ['moc.hr_detail_severity', 'Severity'],
    sinceYear: ['moc.hr_detail_since_year', 'Since'],
    treatingDoctor: ['moc.hr_detail_treating_doctor', 'Treating doctor'],
    dose: ['moc.hr_detail_dose', 'Dose'],
    frequency: ['moc.hr_detail_frequency', 'Frequency'],
    reason: ['moc.hr_detail_reason', 'Reason'],
    duration: ['moc.hr_detail_duration', 'Duration (days)'],
    hospital: ['moc.hr_detail_hospital', 'Hospital'],
    surgeon: ['moc.hr_detail_surgeon', 'Surgeon'],
    childName: ['moc.hr_detail_child_name', 'Child'],
    place: ['moc.hr_detail_place', 'Place'],
    units: ['moc.hr_detail_units', 'Units'],
};

const SEVERITIES = {
    mild: ['moc.hr_severity_mild', 'Mild'],
    moderate: ['moc.hr_severity_moderate', 'Moderate'],
    severe: ['moc.hr_severity_severe', 'Severe'],
};

/* ---------------------------------------------------------------- *
 * Small formatters
 * ---------------------------------------------------------------- */

// occurredAt / endedAt are DATE-ONLY strings ('1994-03-18'). new Date() reads
// those as UTC midnight, which prints the day BEFORE in any negative offset —
// a surgery date that moves by one day is not a rounding error on a medical
// record, so the parts are read straight out of the string.
const formatDay = (value) => {
    if (!value) return null;
    const iso = String(value).slice(0, 10);
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return null;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('en-GB');
};

// created_at is a full timestamp, so the instant is the truth and the local
// calendar day is the right reading of it.
const formatStamp = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('en-GB');
};

const isBlank = (value) =>
    value === null || value === undefined || value === ''
    || (Array.isArray(value) && value.length === 0)
    || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

const prettyKey = (key) =>
    String(key)
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase());

// recordedAt is a full timestamp; the chip reads it as the local time of day,
// 12-hour ("2:30 PM", utils/clock.js), beside the day the visit group already
// names.
const formatTime = (value) => clockTime(value) || null;

// `doctors.specialty` is a mixed JSONB column (a name, a list of names, or
// objects carrying one). Only readable names are printed; a bare master id
// ("SP00003") says nothing to a reader and is left out rather than shown.
const textOf = (value) => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
        if (typeof value.en === 'string') return value.en;
        return textOf(value.name ?? value.specialtyName ?? value.label ?? null);
    }
    return null;
};
const specialtyText = (value) => {
    const names = (Array.isArray(value) ? value : [value])
        .map(textOf)
        .map((name) => (name || '').trim())
        .filter((name) => name && !/^[A-Z]{2,5}\d+$/.test(name));
    return names.length ? [...new Set(names)].join(', ') : null;
};

// One recorded value, read by the type it was RECORDED under (the reading's
// own snapshot), never by the set's live definition — editing a set later
// must not change how an old value reads. The unit is returned apart so the
// card can set it in a lighter weight; only a number carries one.
const formatReadingValue = (item, t) => {
    const { type, value } = item || {};
    const unit = type === 'number' && item?.unit ? String(item.unit) : null;
    if (type === 'yesno' || typeof value === 'boolean') {
        return { text: value ? t('moc.hr_yes', 'Yes') : t('moc.hr_no', 'No'), unit: null };
    }
    if (type === 'date') return { text: formatDay(value) || String(value), unit: null };
    if (Array.isArray(value)) return { text: value.map(String).join(', '), unit: null };
    // An unexpected object is printed rather than dropped, for the same reason
    // an unknown `details` key is: a medical value must not silently vanish.
    if (value && typeof value === 'object') return { text: JSON.stringify(value), unit: null };
    return { text: String(value), unit };
};

/* ---------------------------------------------------------------- *
 * Cards
 * ---------------------------------------------------------------- */

const Badge = ({ icon, tone, children, testId }) => {
    const Icon = icon;
    return (
        <span
            data-testid={testId}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`}
        >
            <Icon className="h-3 w-3 flex-shrink-0" />
            {children}
        </span>
    );
};

const EntryCard = ({ entry }) => {
    const { t } = useTranslation();
    const tr = ([key, fallback]) => t(key, fallback);

    const retracted = entry.status === 'incorrect';
    const started = formatDay(entry.occurredAt);
    const ended = formatDay(entry.endedAt);
    const recordedOn = formatStamp(entry.created_at);
    const subtype = entry.subtype && SUBTYPES[entry.subtype]
        ? tr(SUBTYPES[entry.subtype])
        : (entry.subtype ? prettyKey(entry.subtype) : null);
    const status = !retracted && STATUSES[entry.status] ? tr(STATUSES[entry.status]) : null;

    const details = Object.entries(entry.details || {})
        .filter(([, value]) => !isBlank(value))
        .map(([key, value]) => ({
            key,
            label: DETAIL_LABELS[key] ? tr(DETAIL_LABELS[key]) : prettyKey(key),
            value: key === 'severity' && SEVERITIES[String(value)]
                ? tr(SEVERITIES[String(value)])
                : (typeof value === 'boolean' ? (value ? t('moc.hr_yes', 'Yes') : t('moc.hr_no', 'No')) : String(value)),
        }));

    // "Recorded by Dr Meena · Chitra Clinic · 10 Sep 2026" — who said it, at
    // which hospital, on what day. The hospital is the one the entry was
    // recorded at, never "the hospital being viewed": there is no such thing
    // on this tab.
    const footer = [
        `${t('moc.hr_recorded_by', 'Recorded by')} ${entry.recordedByName || '—'}`,
        entry.recordedAtTenantName,
        recordedOn,
    ].filter(Boolean);

    return (
        <article
            data-testid={`moc-hr-entry-${entry.entryId}`}
            className={`rounded-2xl border p-4 ${retracted
                ? 'border-gray-200 bg-gray-50 opacity-70'
                : 'border-gray-200 bg-white shadow-sm'}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <h4 className={`text-sm font-bold ${retracted ? 'text-gray-500 line-through' : 'text-[#1a365d]'}`}>
                        {entry.title || '—'}
                    </h4>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                        {ended
                            ? `${t('moc.hr_since', 'since')} ${started || t('moc.hr_no_date', 'No date')} · ${t('moc.hr_until', 'until')} ${ended}`
                            : (started || t('moc.hr_no_date', 'No date'))}
                    </p>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-1.5">
                    {subtype && (
                        <span className="inline-flex rounded-full bg-[#4ABDBD]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2D8A8A]">
                            {subtype}
                        </span>
                    )}
                    {status && (
                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                            {status}
                        </span>
                    )}
                    {/* A doctor's own entry comes back `verified` by
                        construction; a patient's is Self-reported until a
                        linked doctor confirms it. The two are never both on
                        a card — that is what makes the badge worth reading. */}
                    {entry.verified ? (
                        <Badge testId="moc-hr-badge-verified" icon={ShieldCheck} tone="border-emerald-200 bg-emerald-50 text-emerald-700">
                            {t('moc.hr_verified', 'Verified')}
                        </Badge>
                    ) : (
                        <Badge testId="moc-hr-badge-self-reported" icon={UserRound} tone="border-gray-200 bg-gray-50 text-gray-500">
                            {t('moc.hr_self_reported', 'Self-reported')}
                        </Badge>
                    )}
                    {retracted && (
                        <Badge testId="moc-hr-badge-incorrect" icon={Ban} tone="border-gray-300 bg-gray-100 text-gray-500">
                            {t('moc.hr_incorrect', 'Marked incorrect')}
                        </Badge>
                    )}
                </div>
            </div>

            {details.length > 0 && (
                <dl className="mt-2.5 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                    {details.map((line) => (
                        <div key={line.key} className="flex min-w-0 gap-1.5 text-[11px]">
                            <dt className="flex-shrink-0 font-semibold text-gray-400">{line.label}</dt>
                            <dd className="min-w-0 break-words text-gray-600">{line.value}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {retracted && entry.incorrectReason && (
                <p data-testid={`moc-hr-incorrect-reason-${entry.entryId}`} className="mt-2.5 rounded-xl bg-gray-100 px-3 py-2 text-[11px] leading-relaxed text-gray-500">
                    {entry.incorrectReason}
                </p>
            )}

            <p className="mt-2.5 flex flex-wrap items-center gap-1 text-[10px] text-gray-400">
                {footer.join(' · ')}
            </p>
        </article>
    );
};

const Section = ({ section, entries }) => {
    const { t } = useTranslation();
    const Icon = section.icon;

    return (
        <section data-testid={`moc-hr-section-${section.id}`} className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3.5">
                <Icon className="h-4 w-4 text-[#4ABDBD]" />
                <h3 className="flex-1 text-sm font-bold text-[#1a365d]">{t(section.label[0], section.label[1])}</h3>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                    {entries.length}
                </span>
            </div>

            {entries.length === 0 ? (
                <p data-testid={`moc-hr-empty-${section.id}`} className="px-5 py-5 text-[11px] text-gray-400">
                    {t(section.empty[0], section.empty[1])}
                </p>
            ) : (
                // Newest first, as the API sorts them (occurredAt DESC, undated
                // last) — the client never re-sorts, or the two orders drift.
                <ol className="relative ml-6 border-l border-gray-200 py-4 pl-5 pr-5">
                    {entries.map((entry) => (
                        <li key={entry.entryId} className="relative pb-3 last:pb-0">
                            <span className="absolute -left-[27px] top-4 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#4ABDBD]" />
                            <EntryCard entry={entry} />
                        </li>
                    ))}
                </ol>
            )}
        </section>
    );
};

/* ---------------------------------------------------------------- *
 * Readings — per visit, read-only
 * ---------------------------------------------------------------- */

// Who took it. The record names clinicians, not a hospital's employees: a
// doctor's reading carries the doctor's name, a nurse's or desk reading says
// "Staff" (the API sends no name for it), a home reading says "Patient".
const recorderOf = (reading, t) => {
    if (reading.recordedRole === 'doctor') {
        return { icon: Stethoscope, text: reading.recordedByName || t('moc.hr_readings_by_doctor', 'Doctor') };
    }
    if (reading.recordedRole === 'patient') {
        return { icon: UserRound, text: t('moc.hr_readings_by_patient', 'Patient') };
    }
    return { icon: UserRound, text: t('moc.hr_readings_by_staff', 'Staff') };
};

const ReadingCard = ({ reading }) => {
    const { t } = useTranslation();
    const items = Array.isArray(reading.items) ? reading.items : [];
    const time = formatTime(reading.recordedAt);
    const recorder = recorderOf(reading, t);
    const RecorderIcon = recorder.icon;

    return (
        <div
            data-testid={`moc-hr-reading-${reading.readingId}`}
            className="rounded-xl border border-[#4ABDBD]/25 bg-[#F7FBFA] px-3.5 py-3"
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex min-w-0 items-center gap-1.5 text-[13px] font-semibold text-[#2D8A8A]">
                    <ClipboardCheck className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{reading.setName || '—'}</span>
                </p>
                <span
                    data-testid={`moc-hr-reading-by-${reading.readingId}`}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500"
                >
                    <RecorderIcon className="h-3 w-3 flex-shrink-0" />
                    {recorder.text}
                    {time && (
                        <>
                            <span className="text-gray-300">·</span>
                            <Clock className="h-3 w-3 flex-shrink-0" />
                            {time}
                        </>
                    )}
                </span>
            </div>

            {items.length > 0 ? (
                // `items` are the snapshot's fields that hold a value, in the
                // set's own order — rendered as sent, never re-sorted.
                <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((item) => {
                        const { text, unit } = formatReadingValue(item, t);
                        return (
                            <div
                                key={item.key}
                                data-testid={`moc-hr-reading-item-${reading.readingId}-${item.key}`}
                                className="flex min-w-0 items-baseline gap-1.5 text-[11px]"
                            >
                                <dt className="flex-shrink-0 font-semibold text-gray-400">{item.label || '—'}</dt>
                                <dd className="min-w-0 break-words font-semibold text-gray-700">
                                    {text}
                                    {unit && <span className="font-normal text-gray-400">{` ${unit}`}</span>}
                                </dd>
                            </div>
                        );
                    })}
                </dl>
            ) : (
                <p className="mt-2 text-[11px] text-gray-400">{t('moc.hr_readings_no_values', 'No value recorded.')}</p>
            )}
        </div>
    );
};

const ReadingVisit = ({ visit }) => {
    const { t } = useTranslation();
    const readings = Array.isArray(visit.readings) ? visit.readings : [];
    const day = formatStamp(visit.date);
    const specialty = specialtyText(visit.doctorSpecialty);
    // A home reading (later phase) is a group of its own with no visit.
    const groupId = visit.appointmentId || `home-${readings[0]?.readingId || 'unknown'}`;

    return (
        <article
            data-testid={`moc-hr-readings-visit-${groupId}`}
            className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
        >
            <h4 className="flex items-center gap-1.5 text-sm font-bold text-[#1a365d]">
                <CalendarDays className="h-3.5 w-3.5 flex-shrink-0 text-[#4ABDBD]" />
                {day || t('moc.hr_no_date', 'No date')}
            </h4>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                {!visit.appointmentId && (
                    <span className="inline-flex items-center gap-1">
                        <House className="h-3 w-3 flex-shrink-0 text-gray-400" />
                        {t('moc.hr_readings_home', 'Home reading')}
                    </span>
                )}
                {visit.hospitalName && (
                    <span className="inline-flex items-center gap-1">
                        <Building2 className="h-3 w-3 flex-shrink-0 text-gray-400" />
                        {visit.hospitalName}
                    </span>
                )}
                {visit.doctorName && (
                    <span className="inline-flex items-center gap-1">
                        <Stethoscope className="h-3 w-3 flex-shrink-0 text-gray-400" />
                        {visit.doctorName}
                        {specialty && <span className="text-gray-400">{` · ${specialty}`}</span>}
                    </span>
                )}
            </p>

            <div className="mt-3 flex flex-col gap-2.5">
                {readings.map((reading) => (
                    <ReadingCard key={reading.readingId} reading={reading} />
                ))}
            </div>
        </article>
    );
};

const ReadingsSection = ({ readings, loading, error, knownCount }) => {
    const { t, i18n } = useTranslation();
    const visits = Array.isArray(readings?.visits) ? readings.visits : [];
    // The record's own `counts.readings` badges the heading before the
    // readings themselves have landed; the readings answer then replaces it.
    const count = typeof readings?.count === 'number'
        ? readings.count
        : (typeof knownCount === 'number' ? knownCount : null);

    let body;
    if (loading && !readings) {
        body = (
            <p data-testid="moc-hr-readings-loading" className="flex items-center gap-2 px-5 py-5 text-[11px] text-gray-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#4ABDBD]" />
                {t('moc.hr_readings_loading', 'Loading readings…')}
            </p>
        );
    } else if (error && !readings) {
        body = (
            <div data-testid="moc-hr-readings-error" className="px-5 py-5">
                <p className="text-[11px] font-semibold text-[#1a365d]">
                    {t('moc.hr_readings_failed', 'Could not load the readings')}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                    {extractErrorMessage(error, '', String(i18n.language || 'en').slice(0, 2))}
                </p>
            </div>
        );
    } else if (visits.length === 0) {
        body = (
            <p data-testid="moc-hr-empty-readings" className="px-5 py-5 text-[11px] text-gray-400">
                {t('moc.hr_readings_empty', 'No check reading on record.')}
            </p>
        );
    } else {
        body = (
            // Newest visit first, as the API sorts them — never re-sorted here.
            <ol className="relative ml-6 border-l border-gray-200 py-4 pl-5 pr-5">
                {visits.map((visit, index) => (
                    <li key={visit.appointmentId || visit.readings?.[0]?.readingId || index} className="relative pb-3 last:pb-0">
                        <span className="absolute -left-[27px] top-4 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#4ABDBD]" />
                        <ReadingVisit visit={visit} />
                    </li>
                ))}
            </ol>
        );
    }

    return (
        <section data-testid="moc-hr-section-readings" className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3.5">
                <Gauge className="h-4 w-4 text-[#4ABDBD]" />
                <h3 className="flex-1 text-sm font-bold text-[#1a365d]">{t('moc.hr_readings', 'Readings')}</h3>
                {count !== null && (
                    <span data-testid="moc-hr-readings-count" className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                        {count}
                    </span>
                )}
            </div>
            <p className="px-5 pt-3 text-[11px] leading-relaxed text-gray-400">
                {t('moc.hr_readings_note', 'Check readings taken during visits, grouped by visit. Each value is shown with the label and unit its set had when it was recorded; a corrected reading shows only its latest version.')}
            </p>
            {body}
        </section>
    );
};

/* ---------------------------------------------------------------- *
 * Care programmes — active and closed, read-only
 * ---------------------------------------------------------------- */

// The four states the server computes (utils/programmeTimeline.js). The page
// never works a state out for itself: the timeline is computed in the
// STARTING hospital's time zone, so a browser elsewhere would only get the
// window edges wrong. A state the API adds later is shown by its own name in
// the neutral tone rather than dropped.
const MILESTONE_STATES = {
    done: {
        icon: CircleCheck, tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        label: ['moc.hr_prog_state_done', 'Done'], count: ['moc.hr_prog_count_done', '{{count}} done'],
    },
    due: {
        icon: Hourglass, tone: 'border-[#4ABDBD]/40 bg-[#4ABDBD]/10 text-[#2D8A8A]',
        label: ['moc.hr_prog_state_due', 'Due'], count: ['moc.hr_prog_count_due', '{{count}} due'],
    },
    late: {
        icon: CircleAlert, tone: 'border-rose-200 bg-rose-50 text-rose-600',
        label: ['moc.hr_prog_state_late', 'Late'], count: ['moc.hr_prog_count_late', '{{count}} late'],
    },
    upcoming: {
        icon: CircleDashed, tone: 'border-gray-200 bg-gray-50 text-gray-500',
        label: ['moc.hr_prog_state_upcoming', 'Upcoming'], count: ['moc.hr_prog_count_upcoming', '{{count}} upcoming'],
    },
};
const STATE_ORDER = ['done', 'due', 'late', 'upcoming'];

const stateOf = (state, t) => {
    const known = MILESTONE_STATES[state];
    if (known) return { icon: known.icon, tone: known.tone, text: t(known.label[0], known.label[1]) };
    return { icon: CircleDashed, tone: 'border-gray-200 bg-gray-50 text-gray-500', text: state ? prettyKey(state) : '—' };
};

const StateChip = ({ state, testId }) => {
    const { t } = useTranslation();
    const { icon, tone, text } = stateOf(state, t);
    return <Badge testId={testId} icon={icon} tone={tone}>{text}</Badge>;
};

// "Week 24 + 3 d" — where the programme stands. A whole week drops the "+ 0".
const weekText = (weeks, days, t) => (Number(days) > 0
    ? t('moc.hr_prog_week_days', 'Week {{weeks}} + {{days}} d', { weeks, days })
    : t('moc.hr_prog_week', 'Week {{weeks}}', { weeks }));

// Where a milestone sits in the programme: "Week 12" when the doctor put it
// on a whole week, "Day 90" otherwise — the builder takes either, and the
// reader sees the one it was most likely typed in. Nothing here assumes a
// programme counts in weeks.
const offsetText = (milestone, t) => {
    const offset = Number(milestone.offsetDays);
    if (offset > 0 && Number(milestone.dueDayOfWeek) === 0 && Number.isFinite(Number(milestone.dueWeek))) {
        return t('moc.hr_prog_week', 'Week {{weeks}}', { weeks: milestone.dueWeek });
    }
    return t('moc.hr_prog_day', 'Day {{day}}', { day: Number.isFinite(offset) ? offset : '—' });
};

// What proved a milestone done — the server picks ONE (hand tick, then a
// reading of the attached set, then a completed visit at the starting
// hospital). The hand tick's note is part of the record projection; the
// clinician who ticked it is not named (the record carries a user id, not
// a name, and an id says nothing to a reader).
const evidenceOf = (evidence, milestone, t) => {
    if (!evidence) return null;
    if (evidence.type === 'manual') {
        return {
            icon: UserCheck,
            text: t('moc.hr_prog_ev_manual', 'Marked done by hand on {{date}}', { date: formatStamp(evidence.at) || formatDay(evidence.date) || '—' }),
            note: evidence.note || null,
        };
    }
    if (evidence.type === 'reading') {
        return {
            icon: ClipboardCheck,
            text: t('moc.hr_prog_ev_reading', 'Reading of {{set}} on {{date}}', {
                set: milestone.setName || t('moc.hr_prog_set_unnamed', 'its check set'),
                date: formatDay(evidence.date) || formatStamp(evidence.recordedAt) || '—',
            }),
            note: null,
        };
    }
    if (evidence.type === 'visit') {
        return {
            icon: CalendarCheck,
            text: t('moc.hr_prog_ev_visit', 'Completed visit on {{date}}', { date: formatDay(evidence.date) || '—' }),
            note: null,
        };
    }
    return { icon: CircleCheck, text: prettyKey(evidence.type || '—'), note: null };
};

const MilestoneRow = ({ milestone, programmeId }) => {
    const { t } = useTranslation();
    const due = formatDay(milestone.dueDate);
    const from = formatDay(milestone.windowStart);
    const to = formatDay(milestone.windowEnd);
    const proof = evidenceOf(milestone.evidence, milestone, t);
    const ProofIcon = proof?.icon;
    const late = milestone.state === 'late';

    return (
        <li
            data-testid={`moc-hr-milestone-${programmeId}-${milestone.key}`}
            className={`rounded-xl border px-3 py-2.5 ${late ? 'border-rose-100 bg-rose-50/40' : 'border-gray-100 bg-white'}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-gray-700">{milestone.name || '—'}</p>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                        <span className="font-semibold text-gray-600">{offsetText(milestone, t)}</span>
                        {due && <span>{` · ${t('moc.hr_prog_due_on', 'due {{date}}', { date: due })}`}</span>}
                        {from && to && (
                            <span className="text-gray-400">
                                {` · ${t('moc.hr_prog_window', 'window {{from}} – {{to}}', { from, to })}`}
                            </span>
                        )}
                    </p>
                </div>
                <StateChip state={milestone.state} testId={`moc-hr-milestone-state-${programmeId}-${milestone.key}`} />
            </div>

            {milestone.setName && (
                <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#4ABDBD]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2D8A8A]">
                    <ClipboardCheck className="h-3 w-3 flex-shrink-0" />
                    {milestone.setName}
                </span>
            )}

            {proof && (
                <div data-testid={`moc-hr-milestone-evidence-${programmeId}-${milestone.key}`} className="mt-1.5 text-[11px] text-gray-500">
                    <p className="flex items-center gap-1">
                        <ProofIcon className="h-3 w-3 flex-shrink-0 text-emerald-600" />
                        {proof.text}
                    </p>
                    {proof.note && (
                        <p className="mt-1 rounded-lg bg-gray-50 px-2.5 py-1.5 text-[11px] leading-relaxed text-gray-600">
                            {proof.note}
                        </p>
                    )}
                </div>
            )}
        </li>
    );
};

const Fact = ({ label, value, testId }) => (
    <div data-testid={testId} className="min-w-0">
        <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
        <dd className="mt-0.5 truncate text-[12px] font-semibold text-gray-700">{value}</dd>
    </div>
);

const ProgrammeCard = ({ programme }) => {
    const { t } = useTranslation();
    const id = programme.patientProgrammeId;
    const tl = programme.timeline || {};
    const milestones = Array.isArray(tl.milestones) ? tl.milestones : [];
    const late = Array.isArray(tl.late) ? tl.late : [];
    const next = tl.next || null;
    const closed = programme.status === 'closed';
    const progress = typeof tl.progress === 'number' ? Math.min(Math.max(tl.progress, 0), 1) : null;
    const percent = progress === null ? null : Math.round(progress * 100);

    // Where the programme stands — frozen at the closing day once closed
    // (the server stops the clock there, so nothing turns late afterwards).
    const standing = tl.started === false
        ? t('moc.hr_prog_not_started', 'Starts {{date}}', { date: formatDay(tl.startDate || programme.startDate) || '—' })
        : weekText(tl.weeks ?? 0, tl.days ?? 0, t);
    const expectedEnd = tl.expectedEnd
        ? formatDay(tl.expectedEnd)
        : t('moc.hr_prog_open_ended', 'Open-ended');
    const counts = tl.counts || null;

    return (
        <article
            data-testid={`moc-hr-programme-${id}`}
            className={`rounded-2xl border p-4 ${closed ? 'border-gray-200 bg-gray-50/60' : 'border-gray-200 bg-white shadow-sm'}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <h4 className="flex items-center gap-1.5 text-sm font-bold text-[#1a365d]">
                        <Milestone className="h-3.5 w-3.5 flex-shrink-0 text-[#4ABDBD]" />
                        <span className="truncate">{programme.name || '—'}</span>
                    </h4>
                    <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                        {programme.hospitalName && (
                            <span className="inline-flex items-center gap-1">
                                <Building2 className="h-3 w-3 flex-shrink-0 text-gray-400" />
                                {programme.hospitalName}
                            </span>
                        )}
                        {programme.doctorName && (
                            <span className="inline-flex items-center gap-1">
                                <Stethoscope className="h-3 w-3 flex-shrink-0 text-gray-400" />
                                {programme.doctorName}
                            </span>
                        )}
                    </p>
                </div>
                {closed ? (
                    <Badge testId={`moc-hr-programme-status-${id}`} icon={Flag} tone="border-gray-300 bg-gray-100 text-gray-500">
                        {t('moc.hr_prog_closed', 'Closed')}
                    </Badge>
                ) : (
                    <Badge testId={`moc-hr-programme-status-${id}`} icon={Route} tone="border-[#4ABDBD]/40 bg-[#4ABDBD]/10 text-[#2D8A8A]">
                        {t('moc.hr_prog_active', 'Active')}
                    </Badge>
                )}
            </div>

            {/* The start date carries the name the doctor gave it ("LMP",
                "Surgery date", …) — the label is the programme's own. */}
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                <Fact
                    testId={`moc-hr-programme-start-${id}`}
                    label={programme.startLabel || t('moc.hr_prog_start', 'Start date')}
                    value={formatDay(programme.startDate) || '—'}
                />
                <Fact
                    testId={`moc-hr-programme-week-${id}`}
                    label={closed ? t('moc.hr_prog_at_close', 'At closing') : t('moc.hr_prog_today', 'Today')}
                    value={standing}
                />
                <Fact
                    testId={`moc-hr-programme-end-${id}`}
                    label={t('moc.hr_prog_expected_end', 'Expected end')}
                    value={expectedEnd}
                />
                {closed ? (
                    <Fact
                        testId={`moc-hr-programme-closed-${id}`}
                        label={t('moc.hr_prog_closed_on', 'Closed on')}
                        value={formatStamp(programme.closedAt) || '—'}
                    />
                ) : (
                    <Fact
                        testId={`moc-hr-programme-done-${id}`}
                        label={t('moc.hr_prog_milestones_done', 'Milestones done')}
                        value={counts ? `${counts.done ?? 0} / ${counts.total ?? milestones.length}` : '—'}
                    />
                )}
            </dl>

            {percent !== null && (
                <div
                    data-testid={`moc-hr-programme-progress-${id}`}
                    role="progressbar"
                    aria-label={t('moc.hr_prog_progress', 'Progress')}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percent}
                    className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
                >
                    <div
                        className={`h-full rounded-full transition-[width] duration-500 ${closed ? 'bg-gray-300' : 'bg-[#4ABDBD]'}`}
                        style={{ width: `${percent}%` }}
                    />
                </div>
            )}

            {closed && (
                <div data-testid={`moc-hr-programme-outcome-${id}`} className="mt-3 rounded-xl border border-gray-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{t('moc.hr_prog_outcome', 'Outcome')}</p>
                    <p className="mt-0.5 text-[12px] leading-relaxed text-gray-700">{programme.outcome || '—'}</p>
                    {programme.lifeEventEntryId && (
                        <p data-testid={`moc-hr-programme-life-event-${id}`} className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-[#2D8A8A]">
                            <Baby className="h-3 w-3 flex-shrink-0" />
                            {t('moc.hr_prog_life_event', 'Added to Life events on closing')}
                        </p>
                    )}
                </div>
            )}

            {/* What is coming up and what is late — for a running programme
                only. The server keeps a late milestone OUT of `next` and lists
                it in `late`, so the same milestone is never shown as both. */}
            {!closed && (next || late.length > 0) && (
                <div className="mt-3 flex flex-col gap-2">
                    {next && (
                        <p data-testid={`moc-hr-programme-next-${id}`} className="flex flex-wrap items-center gap-1.5 rounded-xl border border-[#D7A05C]/30 bg-[#D7A05C]/10 px-3 py-2 text-[11px] text-gray-600">
                            <CalendarClock className="h-3.5 w-3.5 flex-shrink-0 text-[#D7A05C]" />
                            <span className="font-semibold text-gray-700">{t('moc.hr_prog_next', 'Next')}:</span>
                            <span className="font-semibold text-[#1a365d]">{next.name || '—'}</span>
                            {formatDay(next.dueDate) && (
                                <span>{`· ${t('moc.hr_prog_due_on', 'due {{date}}', { date: formatDay(next.dueDate) })}`}</span>
                            )}
                            {next.setName && (
                                <span className="inline-flex items-center gap-1 text-[#2D8A8A]">
                                    <ClipboardCheck className="h-3 w-3 flex-shrink-0" />
                                    {next.setName}
                                </span>
                            )}
                        </p>
                    )}
                    {late.length > 0 && (
                        <div data-testid={`moc-hr-programme-late-${id}`} className="flex flex-wrap items-center gap-1.5">
                            {late.map((m) => (
                                <Badge
                                    key={m.key}
                                    testId={`moc-hr-programme-late-chip-${id}-${m.key}`}
                                    icon={CircleAlert}
                                    tone="border-rose-200 bg-rose-50 text-rose-600"
                                >
                                    {`${m.name || '—'}${formatDay(m.dueDate) ? ` · ${t('moc.hr_prog_due_on', 'due {{date}}', { date: formatDay(m.dueDate) })}` : ''}`}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {counts && (
                <p data-testid={`moc-hr-programme-counts-${id}`} className="mt-3 flex flex-wrap items-center gap-1.5">
                    {STATE_ORDER.filter((state) => Number(counts[state]) > 0).map((state) => {
                        const { tone, count } = MILESTONE_STATES[state];
                        return (
                            <span key={state} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`}>
                                {t(count[0], count[1], { count: Number(counts[state]) })}
                            </span>
                        );
                    })}
                </p>
            )}

            {milestones.length > 0 ? (
                // Ordered by offset, as the server sends them — never
                // re-sorted here, or the page and the timeline would drift.
                <ol data-testid={`moc-hr-programme-milestones-${id}`} className="mt-3 flex flex-col gap-2">
                    {milestones.map((m) => (
                        <MilestoneRow key={m.key} milestone={m} programmeId={id} />
                    ))}
                </ol>
            ) : (
                <p className="mt-3 text-[11px] text-gray-400">{t('moc.hr_prog_no_milestones', 'No milestone in this programme.')}</p>
            )}
        </article>
    );
};

const ProgrammeGroup = ({ id, label, programmes }) => (
    <div data-testid={`moc-hr-programmes-${id}`} className="flex flex-col gap-3">
        <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
            {label}
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-gray-500">
                {programmes.length}
            </span>
        </h4>
        {programmes.map((programme) => (
            <ProgrammeCard key={programme.patientProgrammeId} programme={programme} />
        ))}
    </div>
);

const ProgrammesSection = ({ programmes, loading, error, knownCount }) => {
    const { t, i18n } = useTranslation();
    const active = Array.isArray(programmes?.active) ? programmes.active : [];
    const closed = Array.isArray(programmes?.closed) ? programmes.closed : [];
    // The heading counts RUNNING programmes, the same number the record's own
    // `counts.programmes` carries — which badges it before the answer lands.
    const count = programmes
        ? active.length
        : (typeof knownCount === 'number' ? knownCount : null);

    let body;
    if (loading && !programmes) {
        body = (
            <p data-testid="moc-hr-programmes-loading" className="flex items-center gap-2 px-5 py-5 text-[11px] text-gray-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#4ABDBD]" />
                {t('moc.hr_prog_loading', 'Loading programmes…')}
            </p>
        );
    } else if (error && !programmes) {
        body = (
            <div data-testid="moc-hr-programmes-error" className="px-5 py-5">
                <p className="text-[11px] font-semibold text-[#1a365d]">
                    {t('moc.hr_prog_failed', 'Could not load the programmes')}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                    {extractErrorMessage(error, '', String(i18n.language || 'en').slice(0, 2))}
                </p>
            </div>
        );
    } else if (active.length === 0 && closed.length === 0) {
        body = (
            <p data-testid="moc-hr-empty-programmes" className="px-5 py-5 text-[11px] text-gray-400">
                {t('moc.hr_prog_empty', 'No care programme on record.')}
            </p>
        );
    } else {
        // Running first (newest start first), closed below (newest close
        // first) — the API's order, never re-sorted here.
        body = (
            <div className="flex flex-col gap-5 px-5 py-4">
                {active.length > 0 && (
                    <ProgrammeGroup id="active" label={t('moc.hr_prog_group_active', 'Active')} programmes={active} />
                )}
                {closed.length > 0 && (
                    <ProgrammeGroup id="closed" label={t('moc.hr_prog_group_closed', 'Closed')} programmes={closed} />
                )}
            </div>
        );
    }

    return (
        <section data-testid="moc-hr-section-programmes" className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3.5">
                <Milestone className="h-4 w-4 text-[#4ABDBD]" />
                <h3 className="flex-1 text-sm font-bold text-[#1a365d]">{t('moc.hr_programmes', 'Programmes')}</h3>
                {count !== null && (
                    <span data-testid="moc-hr-programmes-count" className="rounded-full bg-[#4ABDBD]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2D8A8A]">
                        {t('moc.hr_prog_active_count', '{{count}} active', { count })}
                    </span>
                )}
            </div>
            <p className="px-5 pt-3 text-[11px] leading-relaxed text-gray-400">
                {t('moc.hr_prog_note', 'Follow-up programmes a doctor started for this person, each kept as it was when started. The timeline is worked out by the server in the starting hospital’s time zone: a milestone is done when it was ticked by hand, when a reading of its check set was taken in its window, or when a visit at that hospital was completed in its window.')}
            </p>
            {body}
        </section>
    );
};

/* ---------------------------------------------------------------- *
 * The tab
 * ---------------------------------------------------------------- */

const HealthRecordReadOnly = ({ patientId }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const {
        healthRecord, healthRecordLoading, healthRecordError,
        healthReadings, healthReadingsFor, healthReadingsLoading, healthReadingsError,
        healthProgrammes, healthProgrammesFor, healthProgrammesLoading, healthProgrammesError,
    } = useSelector((s) => s.moc);

    useEffect(() => {
        if (!patientId) return;
        // The record first, then its readings and its programmes: three
        // endpoints, three independent answers, so one failing never blanks
        // another.
        dispatch(getHealthRecord(patientId));
        dispatch(getHealthReadings(patientId));
        dispatch(getHealthProgrammes(patientId));
    }, [dispatch, patientId]);

    // Readings and programmes are shown only when they are THIS person's.
    const readingsHere = healthReadingsFor === patientId;
    const programmesHere = healthProgrammesFor === patientId;

    // Nothing has come back yet: the page's own loading voice, not a skeleton
    // of seven empty sections that would read as "this person has no record".
    if (healthRecordLoading && !healthRecord) {
        return (
            <div data-testid="moc-hr-loading" className="flex flex-col items-center justify-center py-4 pt-12">
                <CommonLoader />
                <span className="text-xl text-[#4ABDBD]">{t('patientprofilepage.loading', 'Loading…')}</span>
            </div>
        );
    }

    if (healthRecordError && !healthRecord) {
        return (
            <div data-testid="moc-hr-error" className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
                <HeartPulse className="mx-auto h-7 w-7 text-gray-300" />
                <p className="mt-3 text-sm font-semibold text-[#1a365d]">
                    {t('moc.hr_failed', 'Could not load the health record')}
                </p>
                {/* The API refuses with a { en, fr } pair; printing that
                    object as a React child would take the page down. */}
                <p className="mx-auto mt-1 max-w-lg text-xs leading-relaxed text-gray-500">
                    {extractErrorMessage(healthRecordError, '', String(i18n.language || 'en').slice(0, 2))}
                </p>
            </div>
        );
    }

    const categories = healthRecord?.categories || {};
    const bloodGroup = healthRecord?.patient?.bloodGroup || null;

    return (
        <div data-testid="moc-health-record" className="flex flex-col gap-5">
            <div data-testid="moc-hr-header" className="rounded-2xl border border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <HeartPulse className="h-5 w-5 flex-shrink-0 text-[#4ABDBD]" />
                        <p className="truncate text-sm font-bold text-[#1a365d]">
                            {healthRecord?.patient?.name || t('moc.unnamed', 'Unnamed identity')}
                        </p>
                    </div>

                    {/* Blood group is ONE value on the person, not an entry —
                        §8 is explicit about it — so it is a chip up here and
                        never a card in the Blood section below. */}
                    <span
                        data-testid="moc-hr-blood-group"
                        className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold ${bloodGroup
                            ? 'border-rose-200 bg-rose-50 text-rose-600'
                            : 'border-gray-200 bg-white text-gray-400'}`}
                    >
                        <Droplet className="h-3.5 w-3.5 flex-shrink-0" />
                        {t('moc.hr_blood_group', 'Blood group')}: {bloodGroup || t('moc.hr_blood_group_none', 'not recorded')}
                    </span>
                </div>

                <p className="mt-3 flex items-start gap-2 text-[11px] leading-relaxed text-gray-500">
                    <Building2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                    {t('moc.hr_note', 'One record for the person, wherever they were treated — so there is no hospital to pick here; every entry names the hospital it was recorded at. Read-only: the platform never adds, confirms or retracts a clinical entry, and opening this tab is written to the audit log.')}
                </p>
            </div>

            {SECTIONS.map((section) => (
                <Section key={section.id} section={section} entries={categories[section.id] || []} />
            ))}

            <ReadingsSection
                readings={readingsHere ? healthReadings : null}
                // Until this person's request is in flight, say "loading"
                // rather than flash "no reading on record".
                loading={!readingsHere || healthReadingsLoading}
                error={readingsHere ? healthReadingsError : null}
                knownCount={healthRecord?.counts?.readings}
            />

            <ProgrammesSection
                programmes={programmesHere ? healthProgrammes : null}
                loading={!programmesHere || healthProgrammesLoading}
                error={programmesHere ? healthProgrammesError : null}
                knownCount={healthRecord?.counts?.programmes}
            />
        </div>
    );
};

export default HealthRecordReadOnly;
